import { useState } from 'react'
import { FaCopy, FaDownload, FaFileAlt, FaShareAlt, FaTimes, FaTrashAlt } from 'react-icons/fa'
import Button from '../atoms/Buttons'
import ShareByEmailModal from './ShareByEmailModal'
import { createToast } from '../../utils/Toast'
import { useDownloadQueue } from '../../context/DownloadQueueContext'
import {
  downloadFromUrl,
  formatFileSize,
  getBatchUrls,
} from '../../utils/fileBrowserApi'

const Toast = createToast()

const BATCH_LIMIT = 50

const chunk = (arr, size) => {
  const out = []
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
  return out
}

export default function DownloadQueueDrawer() {
  const { items, removeItem, clearQueue, isDrawerOpen, closeDrawer } =
    useDownloadQueue()
  const [copying, setCopying] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [downloadProgress, setDownloadProgress] = useState('')
  const [showShare, setShowShare] = useState(false)

  const fetchAllUrls = async () => {
    const batches = chunk(items, BATCH_LIMIT)
    const results = []
    for (const batch of batches) {
      results.push(...(await getBatchUrls(batch)))
    }
    return results
  }

  const handleCopyLinks = async () => {
    if (!items.length || copying) return
    setCopying(true)
    try {
      const urlItems = await fetchAllUrls()
      const text = urlItems.map(item => item.url).join('\n')
      await navigator.clipboard.writeText(text)
      Toast.fire({
        icon: 'success',
        title: `${urlItems.length} link${urlItems.length > 1 ? 's' : ''} copied. Private links expire in 15 minutes.`,
      })
    } catch (err) {
      console.error('Copy links failed:', err)
      Toast.fire({ icon: 'error', title: 'Failed to copy links' })
    } finally {
      setCopying(false)
    }
  }

  const handleDownloadAll = async () => {
    if (!items.length || downloading) return
    setDownloading(true)
    try {
      const urlItems = await fetchAllUrls()
      const urlByKey = new Map(urlItems.map(item => [`${item.bucket}:${item.key}`, item.url]))
      let done = 0
      for (const item of items) {
        setDownloadProgress(`${done + 1}/${items.length}`)
        const url = urlByKey.get(`${item.bucket}:${item.key}`)
        if (url) await downloadFromUrl(url, item.name)
        done++
      }
      Toast.fire({ icon: 'success', title: `Downloaded ${done} file${done > 1 ? 's' : ''}` })
    } catch (err) {
      console.error('Download all failed:', err)
      Toast.fire({ icon: 'error', title: 'Failed to download files' })
    } finally {
      setDownloading(false)
      setDownloadProgress('')
    }
  }

  return (
    <>
      {isDrawerOpen && (
        <div
          className="fixed inset-0 bg-black opacity-40 z-40"
          onClick={closeDrawer}
        />
      )}

      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[360px] bg-white shadow-2xl z-50 flex flex-col font-montserrat transform transition-transform duration-300 ${
          isDrawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 m-0">
            Download Queue{items.length > 0 && ` (${items.length})`}
          </h3>
          <button
            type="button"
            onClick={closeDrawer}
            className="text-gray-500 hover:text-[#2b364b] bg-transparent border-none cursor-pointer p-1"
            title="Close"
          >
            <FaTimes size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-3">
          {items.length === 0 ? (
            <p className="text-sm text-gray-500 text-center mt-10">
              Your download queue is empty. Add files from the File Browser.
            </p>
          ) : (
            items.map(item => (
              <div
                key={`${item.bucket}:${item.key}`}
                className="flex items-start gap-3 py-3 border-b border-gray-100"
              >
                <FaFileAlt className="text-blue-500 shrink-0 mt-1" size={15} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate m-0" title={item.name}>
                    {item.name}
                  </p>
                  <p className="text-xs text-gray-500 truncate m-0" title={item.key}>
                    {item.key}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${
                        item.bucket === 'public'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {item.bucket}
                    </span>
                    <span className="text-xs text-gray-400">
                      {formatFileSize(item.size)}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  title="Remove from queue"
                  onClick={() => removeItem(item)}
                  className="text-gray-400 hover:text-red-500 bg-transparent border-none cursor-pointer p-1 shrink-0"
                >
                  <FaTimes size={13} />
                </button>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="px-5 py-4 border-t border-gray-200 space-y-2">
            <Button
              onClick={() => setShowShare(true)}
              AppendIcon={FaShareAlt}
              primary
              disabled={copying || downloading}
              className="create-saddle-btn !w-full cursor-pointer"
            >
              Share via Email
            </Button>
            <Button
              onClick={handleCopyLinks}
              AppendIcon={FaCopy}
              primary
              loading={copying}
              className="create-saddle-btn !w-full cursor-pointer"
            >
              Copy Links
            </Button>
            <Button
              onClick={handleDownloadAll}
              AppendIcon={FaDownload}
              primary
              loading={downloading}
              className="create-saddle-btn !w-full cursor-pointer"
            >
              {downloading && downloadProgress
                ? `Downloading ${downloadProgress}…`
                : 'Download All'}
            </Button>
            <Button
              onClick={() => {
                clearQueue()
                Toast.fire({ icon: 'success', title: 'Download queue cleared' })
              }}
              AppendIcon={FaTrashAlt}
              primaryOutline
              disabled={copying || downloading}
              className="create-saddle-btn !w-full cursor-pointer"
            >
              Clear Queue
            </Button>
          </div>
        )}
      </div>

      {showShare && (
        <ShareByEmailModal items={items} onClose={() => setShowShare(false)} />
      )}
    </>
  )
}
